import { checkQuorum } from './quorum.ts';
import { isHalf, minCount } from './ratio.ts';
import type { Evaluation, EvaluationContext, RuleSet, VoteCount, VotingMode } from './types.ts';

export class UnknownMatterError extends Error {
  readonly matterId: string;

  constructor(matterId: string) {
    super(`El asunto "${matterId}" no existe en el RuleSet.`);
    this.name = 'UnknownMatterError';
    this.matterId = matterId;
  }
}

/** Modalidad con la que se vota un tipo de asunto: la suya o, si no tiene, la del RuleSet. */
export function votingMode(rules: RuleSet, matterId: string): VotingMode {
  const matter = rules.matters.find((m) => m.id === matterId);
  if (!matter) throw new UnknownMatterError(matterId);
  return matter.mode ?? rules.mode;
}

export function evaluate(votes: VoteCount, rules: RuleSet, matterId: string, ctx: EvaluationContext): Evaluation {
  const matter = rules.matters.find((m) => m.id === matterId);
  const maj = matter && rules.majorities[matter.majority];
  if (!maj) throw new UnknownMatterError(matterId);

  const { for: f, against: a, abstain: ab } = votes;
  const base = maj.base === 'total' ? ctx.total
    : maj.base === 'present' ? f + a + ab
    : f + a + (maj.abstentionsCount ? ab : 0);
  const need = minCount(base, maj.threshold, maj.strict);
  const tie = maj.base !== 'total' && isHalf(maj.threshold) && f === a;

  // El quórum al cerrar va primero: sin él, la votación no tiene efecto, haya empate o no.
  const atClose = rules.quorum.atClose ?? 'none';
  if (atClose !== 'none') {
    if (atClose === 'present' && ctx.present === undefined) {
      throw new TypeError('La regla revisa el quórum con los presentes al cerrar: falta ctx.present.');
    }
    const count = atClose === 'ballots' ? f + a + ab : ctx.present!;
    const quorum = checkQuorum(count, ctx.total, rules.quorum);
    if (!quorum.ok) {
      const what = atClose === 'ballots'
        ? (count === 1 ? 'voto emitido' : 'votos emitidos')
        : (count === 1 ? 'presente' : 'presentes');
      return {
        status: 'no-quorum', approved: false, base, need,
        reason: `Sin quórum al cerrar: ${count} ${what} de ${quorum.need} necesarios. La votación no tiene efecto.`,
      };
    }
  }

  if (base === 0) return { status: 'rejected', approved: false, base, need, reason: 'No hay votos que contar.' };
  if (tie) {
    if (rules.tie === 'repeat') return { status: 'repeat', approved: false, base, need, reason: 'Empate: se repite la votación.' };
    if (rules.tie === 'reject') return { status: 'rejected', approved: false, base, need, reason: 'Empate: la propuesta se desecha.' };
    if (ctx.casting) {
      const ok = ctx.casting === 'for';
      return {
        status: ok ? 'approved' : 'rejected', approved: ok, base, need,
        reason: 'Empate: voto de calidad de la presidencia ' + (ok ? 'a favor.' : 'en contra.'),
      };
    }
    return { status: 'needs-casting', approved: false, base, need, reason: 'Empate: falta el voto de calidad de la presidencia.' };
  }

  const ok = f >= need;
  return {
    status: ok ? 'approved' : 'rejected', approved: ok, base, need,
    reason: ok
      ? `${f} a favor alcanzan el mínimo de ${need}.`
      : `${f} a favor no alcanzan el mínimo de ${need}.`,
  };
}
